/**
 * Utility to generate unique component names, appending (2), (3), etc. when duplicates exist.
 * Also provides deterministic component color normalization.
 * Used across key extraction, autocount, and manual component creation flows.
 */

// Canonical colors for phase 1 takeoff coloring.
export const RECEPTACLE_COLOR = 'rgba(34, 197, 94, 0.62)'; // green
export const TELE_DATA_COLOR = 'rgba(59, 130, 246, 0.62)'; // blue
export const SWITCH_COLOR = 'rgba(236, 72, 153, 0.62)'; // pink
export const POWER_COLOR = RECEPTACLE_COLOR;
export const LIGHTING_COLOR = 'rgba(245, 158, 11, 1)'; // deterministic fallback for lighting
const RECEPTACLE_TYPE_COLORS = {
  duplex_receptacle: 'rgba(0, 255, 106, 0.78)', // neon spring green
  gfci_receptacle: 'rgba(0, 224, 188, 0.78)', // bright aqua green
  dedicated_receptacle: 'rgba(24, 170, 24, 0.78)', // bold dark green
  halfswitch_specific_circuit_receptacle: 'rgba(157, 255, 26, 0.78)', // vivid yellow green
  quadruplex_receptacle: 'rgba(239, 92, 70, 0.78)', // bold reddish
} as const;

/**
 * Component metadata needed for deterministic color selection.
 */
export type ComponentColorDescriptor = {
  name?: string;
  symbol?: string;
  componentType?: string;
  symbolType?: string;
};

const LIGHTING_HUE_STEP = 10;
const LIGHTING_HUES_PER_LOOP = Math.floor(360 / LIGHTING_HUE_STEP); // 36
const LIGHTING_BASE_HUE = 0;
const LIGHTING_LOOP_TONES = [
  { saturation: 96, lightness: 50 }, // primary pass
  { saturation: 84, lightness: 56 }, // lighter second pass
  { saturation: 88, lightness: 42 }, // deeper third pass
];
const LIGHTING_TOKEN_ORDER = new Map<string, number>();

function normalizeLower(value?: string): string {
  return String(value || '').trim().toLowerCase();
}

function registerLightingToken(token: string): number {
  const existing = LIGHTING_TOKEN_ORDER.get(token);
  if (existing !== undefined) return existing;
  const nextIndex = LIGHTING_TOKEN_ORDER.size;
  LIGHTING_TOKEN_ORDER.set(token, nextIndex);
  return nextIndex;
}

function getLightingToken(component: ComponentColorDescriptor): string {
  const lightingSymbol = String(component.symbol || '').trim();
  const lightingName = String(component.name || '').trim();
  if (!(lightingSymbol || lightingName)) return '';
  return `${lightingSymbol}|${lightingName}`.toUpperCase();
}

function hslToRgb(hue: number, saturationPct: number, lightnessPct: number): [number, number, number] {
  const s = saturationPct / 100;
  const l = lightnessPct / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hPrime = hue / 60;
  const x = c * (1 - Math.abs((hPrime % 2) - 1));

  let r1 = 0;
  let g1 = 0;
  let b1 = 0;

  if (hPrime >= 0 && hPrime < 1) {
    r1 = c;
    g1 = x;
  } else if (hPrime >= 1 && hPrime < 2) {
    r1 = x;
    g1 = c;
  } else if (hPrime >= 2 && hPrime < 3) {
    g1 = c;
    b1 = x;
  } else if (hPrime >= 3 && hPrime < 4) {
    g1 = x;
    b1 = c;
  } else if (hPrime >= 4 && hPrime < 5) {
    r1 = x;
    b1 = c;
  } else {
    r1 = c;
    b1 = x;
  }

  const m = l - c / 2;
  return [
    Math.round((r1 + m) * 255),
    Math.round((g1 + m) * 255),
    Math.round((b1 + m) * 255),
  ];
}

function getLightingColorFromToken(token?: string): string {
  const normalizedToken = String(token || '').trim().toUpperCase();
  if (!normalizedToken) return LIGHTING_COLOR;

  // Deterministic sequence: advance 10 degrees each token around the full wheel.
  // After one loop, reuse hues with alternate tone bands to avoid exact repeats.
  const tokenIndex = registerLightingToken(normalizedToken);
  const hue = (LIGHTING_BASE_HUE + ((tokenIndex % LIGHTING_HUES_PER_LOOP) * LIGHTING_HUE_STEP)) % 360;
  const loopIndex = Math.floor(tokenIndex / LIGHTING_HUES_PER_LOOP);
  const tone = LIGHTING_LOOP_TONES[loopIndex % LIGHTING_LOOP_TONES.length];
  const saturation = tone.saturation;
  const lightness = tone.lightness;
  const [r, g, b] = hslToRgb(hue, saturation, lightness);
  return `rgba(${r}, ${g}, ${b}, 1)`;
}

function isLightingComponent(component: ComponentColorDescriptor): boolean {
  const componentType = normalizeLower(component.componentType);
  const symbolType = normalizeLower(component.symbolType);
  return componentType === 'lighting' || symbolType === 'lighting';
}

function isTeleDataComponent(component: ComponentColorDescriptor): boolean {
  const componentType = normalizeLower(component.componentType);
  const name = normalizeLower(component.name);
  const symbol = normalizeLower(component.symbol);
  const haystack = `${name} ${symbol}`;
  return (
    componentType === 'telecom' ||
    componentType === 'low voltage' ||
    componentType.includes('telecom') ||
    componentType.includes('data') ||
    haystack.includes('tele') ||
    haystack.includes('data') ||
    haystack.includes('communication') ||
    haystack.includes('low voltage')
  );
}

function isReceptacleComponent(component: ComponentColorDescriptor): boolean {
  const componentType = normalizeLower(component.componentType);
  const name = normalizeLower(component.name);
  const symbol = normalizeLower(component.symbol);
  const haystack = `${name} ${symbol}`;
  return (
    componentType === 'receptacles' ||
    componentType === 'power' ||
    componentType.includes('receptacle') ||
    haystack.includes('receptacle') ||
    haystack.includes('duplex') ||
    haystack.includes('gfci') ||
    haystack.includes('quadruplex')
  );
}

function getReceptacleTypeKey(component: ComponentColorDescriptor): keyof typeof RECEPTACLE_TYPE_COLORS | null {
  const name = normalizeLower(component.name).replace(/[_-]/g, ' ');
  const symbol = normalizeLower(component.symbol).replace(/[_-]/g, ' ');
  const haystack = `${name} ${symbol}`;
  if (!haystack.trim()) return null;

  if (haystack.includes('gfci') || haystack.includes('gfi') || haystack.includes('ground fault')) {
    return 'gfci_receptacle';
  }
  if (haystack.includes('halfswitch') || haystack.includes('half switch') || haystack.includes('switched half')) {
    return 'halfswitch_specific_circuit_receptacle';
  }
  if (
    haystack.includes('dedicated') ||
    haystack.includes('individual circuit') ||
    haystack.includes('specified circuit') ||
    haystack.includes('isolated') ||
    haystack.includes('specific circuit') ||
    haystack.includes('special circuit') ||
    haystack.includes('emergency')
  ) {
    return 'dedicated_receptacle';
  }
  if (
    haystack.includes('quadruplex') ||
    haystack.includes('quad') ||
    haystack.includes('double duplex') ||
    haystack.includes('fourplex')
  ) {
    return 'quadruplex_receptacle';
  }
  if (haystack.includes('receptacle') || haystack.includes('duplex') || haystack.includes('outlet')) {
    return 'duplex_receptacle';
  }

  return null;
}

function isSwitchComponent(component: ComponentColorDescriptor): boolean {
  const componentType = normalizeLower(component.componentType);
  const name = normalizeLower(component.name);
  const symbol = normalizeLower(component.symbol);
  const haystack = `${name} ${symbol}`;
  if (isReceptacleComponent(component)) return false;
  return (
    componentType === 'switches' ||
    componentType.includes('switch') ||
    haystack.includes('switch') ||
    haystack.includes('dimmer') ||
    haystack.includes('toggle')
  );
}

/**
 * Returns canonical color by component family.
 * - Receptacles: type-based greens (duplex / gfci / dedicated / halfswitch / quadruplex)
 * - Tele/Data: blue
 * - Switches: pink
 * - Lighting: deterministic full-wheel color per lighting code/symbol
 * - Other power components: green
 */
export function getComponentColor(component: ComponentColorDescriptor): string {
  if (isLightingComponent(component)) {
    return getLightingColorFromToken(getLightingToken(component));
  }
  if (isTeleDataComponent(component)) return TELE_DATA_COLOR;
  if (isSwitchComponent(component)) return SWITCH_COLOR;
  if (isReceptacleComponent(component)) {
    const receptacleType = getReceptacleTypeKey(component);
    if (receptacleType) return RECEPTACLE_TYPE_COLORS[receptacleType];
    return RECEPTACLE_COLOR;
  }
  return POWER_COLOR;
}

/**
 * Normalizes component colors to canonical phase-1 values.
 * Call this when loading project_key from the database.
 */
export function normalizeComponentColors<T extends { color?: string; customColor?: boolean; componentType?: string; symbolType?: string; name?: string; symbol?: string; description?: string; referenceImage?: string; isStandardClass?: boolean; pages?: any[]; totalCount?: number }>(
  components: T[]
): T[] {
  // Rebuild lighting token order deterministically from the incoming list so
  // repeated renders/sessions keep the same hue sequence for the same key order.
  LIGHTING_TOKEN_ORDER.clear();
  components.forEach((comp) => {
    if (!isLightingComponent(comp as unknown as ComponentColorDescriptor)) return;
    const token = getLightingToken(comp as unknown as ComponentColorDescriptor);
    if (token) registerLightingToken(token);
  });

  return components.map(comp => {
    const correctColor = getComponentColor(comp);
    if (comp.color !== correctColor) {
      return { ...comp, color: correctColor };
    }
    return comp;
  });
}

/**
 * Returns a unique component name by appending (2), (3), etc. if the name already exists.
 * Comparison is case-insensitive to avoid "Duplex Receptacle" vs "duplex receptacle" collisions.
 *
 * @param desiredName - The name we want to use
 * @param existingNames - Array of names already taken
 * @returns A unique name, possibly with a suffix like " (2)"
 */
export function getUniqueComponentName(
  desiredName: string,
  existingNames: string[]
): string {
  const lowerNames = existingNames.map(n => n.toLowerCase());

  if (!lowerNames.includes(desiredName.toLowerCase())) {
    return desiredName;
  }

  let counter = 2;
  while (lowerNames.includes(`${desiredName} (${counter})`.toLowerCase())) {
    counter++;
  }
  return `${desiredName} (${counter})`;
}

/**
 * Deduplicates an array of items that have a `name` property, appending suffixes to duplicates.
 * Preserves the original order. The first occurrence keeps its original name.
 *
 * @param items - Array of objects with at least a `name` property
 * @returns New array with unique names
 */
export function deduplicateComponentNames<T extends { name: string }>(
  items: T[]
): T[] {
  const seenNames: string[] = [];

  return items.map(item => {
    const uniqueName = getUniqueComponentName(item.name, seenNames);
    seenNames.push(uniqueName);
    if (uniqueName !== item.name) {
      return { ...item, name: uniqueName };
    }
    return item;
  });
}
